import { Routes, Route, Navigate } from 'react-router-dom'
import { Suspense, lazy } from 'react'
import RootLayout from './components/layout/RootLayout'
import PageLoader from './components/ui/PageLoader'

const ChatFAB = lazy(() => import('./features/support/ChatFAB'))

const HomePage = lazy(() => import('./routes/index'))
const BranchesPage = lazy(() => import('./routes/branches/index'))
const BranchDetailPage = lazy(() => import('./routes/branches/[slug]'))
const ServicesPage = lazy(() => import('./routes/services/index'))
const ServiceDetailPage = lazy(() => import('./routes/services/[slug]'))
const ConditionsPage = lazy(() => import('./routes/conditions/index'))
const ConditionDetailPage = lazy(() => import('./routes/conditions/[slug]'))
const TeamPage = lazy(() => import('./routes/team/index'))
const TeamMemberPage = lazy(() => import('./routes/team/[id]'))
const AboutPage = lazy(() => import('./routes/about'))
const BlogPage = lazy(() => import('./routes/blog/index'))
const BlogPostPage = lazy(() => import('./routes/blog/[slug]'))
const ContactPage = lazy(() => import('./routes/contact'))
const InsurancePage = lazy(() => import('./routes/insurance'))
const ForPhysiciansPage = lazy(() => import('./routes/for-physicians'))

const ScreeningLandingPage = lazy(() => import('./routes/screening/index'))
const ScreeningResultPage = lazy(() => import('./routes/screening/result'))
const ScreeningUrgentPage = lazy(() => import('./routes/screening/urgent'))

const TriageWizardPage = lazy(() => import('./routes/triage/index'))
const TriageResultPage = lazy(() => import('./routes/triage/result'))

const ActivityPlanPage = lazy(() => import('./routes/activity-plan/index'))

const BookLayout = lazy(() => import('./routes/book/layout'))
const BookBranchPage = lazy(() => import('./routes/book/index'))
const BookServicePage = lazy(() => import('./routes/book/service'))
const BookTherapistPage = lazy(() => import('./routes/book/therapist'))
const BookSlotPage = lazy(() => import('./routes/book/slot'))
const BookConfirmPage = lazy(() => import('./routes/book/confirm'))
const BookSuccessPage = lazy(() => import('./routes/book/success'))

const LoginPage = lazy(() => import('./routes/auth/login'))

const AdminLayout = lazy(() => import('./routes/admin/layout'))
const AdminDashboard = lazy(() => import('./routes/admin/index'))
const AdminAppointments = lazy(() => import('./routes/admin/appointments'))
const AdminPatients = lazy(() => import('./routes/admin/patients'))
const AdminStaff = lazy(() => import('./routes/admin/staff'))
const AdminBranches = lazy(() => import('./routes/admin/branches'))
const AdminInvoices = lazy(() => import('./routes/admin/invoices'))
const AdminReviews = lazy(() => import('./routes/admin/reviews'))
const AdminLeads = lazy(() => import('./routes/admin/leads'))
const AdminReportDrafter = lazy(() => import('./routes/admin/report-drafter'))

const PortalLayout = lazy(() => import('./routes/portal/layout'))
const PortalDashboard = lazy(() => import('./routes/portal/index'))
const PortalAppointments = lazy(() => import('./routes/portal/appointments'))
const PortalProgress = lazy(() => import('./routes/portal/progress'))
const PortalInvoices = lazy(() => import('./routes/portal/invoices'))
const PortalMessages = lazy(() => import('./routes/portal/messages'))
const PortalProfile = lazy(() => import('./routes/portal/profile'))

export default function App() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public site */}
        <Route element={<RootLayout />}>
          <Route index element={<HomePage />} />
          <Route path="branches" element={<BranchesPage />} />
          <Route path="branches/:slug" element={<BranchDetailPage />} />
          <Route path="services" element={<ServicesPage />} />
          <Route path="services/:slug" element={<ServiceDetailPage />} />
          <Route path="conditions" element={<ConditionsPage />} />
          <Route path="conditions/:slug" element={<ConditionDetailPage />} />
          <Route path="team" element={<TeamPage />} />
          <Route path="team/:id" element={<TeamMemberPage />} />
          <Route path="about" element={<AboutPage />} />
          <Route path="blog" element={<BlogPage />} />
          <Route path="blog/:slug" element={<BlogPostPage />} />
          <Route path="contact" element={<ContactPage />} />
          <Route path="insurance" element={<InsurancePage />} />
          <Route path="for-physicians" element={<ForPhysiciansPage />} />
          <Route path="screening" element={<ScreeningLandingPage />} />
          <Route path="screening/result/:id" element={<ScreeningResultPage />} />
          <Route path="screening/urgent" element={<ScreeningUrgentPage />} />
          <Route path="triage" element={<TriageWizardPage />} />
          <Route path="triage/result" element={<TriageResultPage />} />
          <Route path="activity-plan" element={<ActivityPlanPage />} />
        </Route>

        {/* Booking wizard */}
        <Route path="book" element={<BookLayout />}>
          <Route index element={<BookBranchPage />} />
          <Route path="service" element={<BookServicePage />} />
          <Route path="therapist" element={<BookTherapistPage />} />
          <Route path="slot" element={<BookSlotPage />} />
          <Route path="confirm" element={<BookConfirmPage />} />
          <Route path="success" element={<BookSuccessPage />} />
        </Route>

        {/* Auth */}
        <Route path="auth">
          <Route path="login" element={<LoginPage />} />
          <Route index element={<Navigate to="login" replace />} />
        </Route>

        {/* Patient portal */}
        <Route path="portal" element={<PortalLayout />}>
          <Route index element={<PortalDashboard />} />
          <Route path="appointments" element={<PortalAppointments />} />
          <Route path="progress" element={<PortalProgress />} />
          <Route path="invoices" element={<PortalInvoices />} />
          <Route path="messages" element={<PortalMessages />} />
          <Route path="profile" element={<PortalProfile />} />
        </Route>

        {/* Admin panel */}
        <Route path="admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboard />} />
          <Route path="appointments" element={<AdminAppointments />} />
          <Route path="patients" element={<AdminPatients />} />
          <Route path="staff" element={<AdminStaff />} />
          <Route path="branches" element={<AdminBranches />} />
          <Route path="invoices" element={<AdminInvoices />} />
          <Route path="reviews" element={<AdminReviews />} />
          <Route path="leads" element={<AdminLeads />} />
          <Route path="report-drafter" element={<AdminReportDrafter />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <ChatFAB />
    </Suspense>
  )
}
